import { useEffect, useMemo, useRef, useState } from 'react';
import {
	BanknotesIcon,
	ReceiptPercentIcon,
	MapIcon,
	BoltIcon,
	GlobeAmericasIcon,
	ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
	assembleKeyMetrics,
	computeGrantsAmount,
	GRANTS_KEY_METRIC_FALLBACK,
	REFERRALS_KEY_METRIC_POTENTIAL,
	SUMMARY_PRIORITY_GRANTS_CAP,
	parseSoftwareIsEstimateFlag,
	readCachedMetricsFromCompany,
	computeFinancing,
	computeAi,
	sumEarningsTotal,
} from '../utils/keyMetricsCompute.js';
import { fetchKeyMetrics, softPatchKeyMetrics } from '../utils/keyMetricsApi.js';

export {
	GRANTS_KEY_METRIC_FALLBACK,
	REFERRALS_KEY_METRIC_POTENTIAL,
	SUMMARY_PRIORITY_GRANTS_CAP,
	grantAmountForSummaryTotal,
} from '../utils/keyMetricsCompute.js';

/** Round-dollar compare — ignore float noise when deciding to write Airtable. */
export function keyMetricAmountsDiffer(a, b) {
	if (a == null && b == null) return false;
	if (a == null || b == null) return true;
	return Math.round(Number(a)) !== Math.round(Number(b));
}

/**
 * Build a company-cache patch when the live amount on a metric's page differs from cache.
 * Returns null when nothing to write.
 */
export function buildActivePageMetricsPatch(metricId, { live, cached }) {
	if (!metricId || !live) return null;
	const patch = {};

	if (metricId === 'financing') {
		const next = live.financing;
		const prev = cached?.financing;
		if (next != null && Number.isFinite(Number(next)) && keyMetricAmountsDiffer(next, prev)) {
			patch.financing = Number(next);
		}
	}

	if (metricId === 'ai') {
		const next = live.ai;
		const prev = cached?.ai;
		if (next != null && Number.isFinite(Number(next)) && keyMetricAmountsDiffer(next, prev)) {
			patch.ai = Number(next);
		}
	}

	if (metricId === 'software') {
		const soft = live.software;
		if (soft && !soft.pending && soft.amount != null && Number.isFinite(Number(soft.amount))) {
			const prev = cached?.software?.amount ?? cached?.software;
			if (keyMetricAmountsDiffer(soft.amount, prev)) {
				patch.software = Number(soft.amount);
				patch.softwareIsEstimate = Boolean(soft.isEstimate);
			}
		}
	}

	if (metricId === 'grants') {
		const next = live.grants;
		const prev = cached?.grants;
		if (next != null && Number.isFinite(Number(next)) && keyMetricAmountsDiffer(next, prev)) {
			patch.grants = Number(next);
		}
	}

	if (metricId === 'earnings') {
		const soft = live.software?.amount || 0;
		const softPending = Boolean(live.software?.pending);
		const grantsReady = live.grants != null;
		if (!softPending && grantsReady) {
			const next = sumEarningsTotal({
				financing: live.financing,
				software: soft,
				grants: live.grants,
				ai: live.ai,
			});
			const prev = cached?.earnings;
			if (Number.isFinite(next) && keyMetricAmountsDiffer(next, prev)) {
				patch.earnings = next;
				if (live.financing != null) patch.financing = Number(live.financing);
				if (live.ai != null) patch.ai = Number(live.ai);
				if (live.grants != null) patch.grants = Number(live.grants);
				patch.software = Number(soft);
				patch.softwareIsEstimate = Boolean(live.software?.isEstimate);
			}
		}
	}

	return Object.keys(patch).length ? patch : null;
}

/** Accent colours (left border) — same family as GrantsCalendar / GrantsChart palette. */
export const KEY_METRIC_ACCENTS = {
	earnings: '#1f2937',
	financing: '#825c79',
	software: '#849e96',
	grants: '#A7C8BE',
	revenue: '#4B0082',
	ai: '#7CA5CF',
	referrals: '#C4787A',
};

export const KEY_METRIC_ITEMS = [
	{
		id: 'earnings',
		label: 'Company Earnings Estimate',
		title: 'Company Earnings Estimate',
		path: '/company-earnings-estimate',
		icon: ChartBarIcon,
		tooltip:
			'Open the company earnings estimate flow. Amount shown is financing + software + grants + AI upgrades combined.',
	},
	{
		id: 'financing',
		label: 'Equity & Private Financing',
		title: 'Financing',
		path: '/financing',
		icon: BanknotesIcon,
		tooltip: 'Calculated as 40% of annual revenue minus existing debt.',
	},
	{
		id: 'software',
		label: 'Software Savings',
		title: 'Software Savings',
		path: '/software-savings',
		icon: ReceiptPercentIcon,
		tooltip:
			'Based on available vendor discounts, partnerships, reducing unused software, negotiation, bulk pricing, and more. Companies typically realize 10-20% of this amount in actual savings.',
	},
	{
		id: 'grants',
		label: 'Grants & Tax Credits',
		title: 'Grants: Qualified & Available',
		path: '/grants-section',
		icon: MapIcon,
		tooltip: `Estimated total for up to ${SUMMARY_PRIORITY_GRANTS_CAP} prioritized opportunities (same ordering as the Summary grant list), not every program that passes the automatic filter. Open Grants to review or narrow matches.`,
	},
	{
		id: 'ai',
		label: 'AI & Revenue Upgrades',
		title: 'AI Automations',
		path: '/ai-automations',
		icon: BoltIcon,
		tooltip:
			'Based on an average of $22,000 potential savings per employee annually through AI and automation. Most companies achieve 25 - 50% of this amount in their first year of implementation.',
	},
	{
		id: 'referrals',
		label: 'Receive Referrals & Earn',
		title: 'Refer & Earn',
		path: '/my-network',
		icon: GlobeAmericasIcon,
		tooltip:
			'Potential earnings from referring companies (15% of first 12 months of fees). Uses your referral payouts when available.',
	},
];

/** Routes where the sticky key-metrics nav should appear (Layout child routes). */
export const KEY_METRICS_NAV_PATHS = [
	'/',
	'/grants-section',
	'/software-savings',
	'/revenue-upgrades',
	'/ai-automations',
	'/financing',
	'/my-network',
];

export function keyMetricIdFromPathname(pathname) {
	if (pathname.startsWith('/company-earnings-estimate')) return 'earnings';
	if (pathname.startsWith('/software-savings')) return 'software';
	if (pathname.startsWith('/grants-section')) return 'grants';
	if (pathname.startsWith('/revenue-upgrades')) return 'revenue';
	if (pathname.startsWith('/ai-automations')) return 'ai';
	if (pathname.startsWith('/financing')) return 'financing';
	if (pathname.startsWith('/my-network')) return 'referrals';
	if (pathname === '/' || pathname === '/home') return null;
	return 'grants';
}

export function shouldShowKeyMetricsNav(pathname) {
	return KEY_METRICS_NAV_PATHS.some(
		(p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`))
	);
}

/**
 * Local/offline compute (Summary + fallback). Prefer useCachedKeyMetricAmounts in the nav.
 * Never invents the $2.1M grants fallback as a live total — returns null for grants when unknown.
 */
export function useKeyMetricAmounts({
	company,
	grants = [],
	matchedDiscounts = [],
	techSavings = 0,
	loading = false,
	grantLoading = false,
	defaultRevenue = 100000,
	defaultEmployees = 10,
}) {
	const metrics = useMemo(() => {
		if (!company?.fields) {
			return {
				loading: true,
				financing: 0,
				software: { pending: true, amount: 0, isEstimate: false },
				grants: null,
				earnings: null,
				revenue: 0,
				ai: 0,
				referrals: REFERRALS_KEY_METRIC_POTENTIAL,
			};
		}

		const assembled = assembleKeyMetrics({
			companyFields: company.fields,
			grants: grants?.length ? grants : null,
			matchedDiscounts,
			techSavings,
			preferCache: true,
		});

		const softwarePending =
			Boolean(String(company.fields.Technologies ?? '').trim()) &&
			matchedDiscounts.length === 0 &&
			(loading || grantLoading) &&
			assembled.source.software === 'estimate';

		return {
			loading: loading || grantLoading,
			financing: assembled.financing,
			software: softwarePending
				? { pending: true, amount: 0, isEstimate: false }
				: {
						pending: false,
						amount: assembled.software,
						isEstimate: assembled.softwareIsEstimate,
				  },
			grants: assembled.grants,
			earnings: assembled.earnings,
			revenue: company.fields.AnnualRevenue
				? company.fields.AnnualRevenue * 0.243 * 5
				: 0,
			ai: assembled.ai,
			referrals: assembled.referrals,
		};
	}, [
		company,
		grants,
		matchedDiscounts,
		techSavings,
		loading,
		grantLoading,
		defaultRevenue,
		defaultEmployees,
	]);

	return metrics;
}

/**
 * Fast path for KeyMetricsNav: seed from company KeyMetric* fields, then /api/key-metrics.
 * Also soft-patches company when the active metric page's live amount differs from cache.
 */
export function useCachedKeyMetricAmounts({
	website,
	getToken,
	company,
	enabled = true,
	activeMetricId = null,
	live = null,
}) {
	const seedFromCompany = (companyRecord) => {
		const fields = companyRecord?.fields;
		if (!fields) return null;
		const cached = readCachedMetricsFromCompany(fields);
		const financing = cached.financing ?? computeFinancing(fields);
		const ai = cached.ai ?? computeAi(fields);
		const softwareAmount = cached.software;
		const grants = cached.grants;
		const earnings =
			cached.earnings ??
			(financing || ai || softwareAmount != null || grants != null
				? sumEarningsTotal({
						financing,
						software: softwareAmount ?? 0,
						grants: grants ?? 0,
						ai,
				  })
				: null);
		return {
			loading: false,
			financing,
			software: {
				pending: softwareAmount == null,
				amount: softwareAmount ?? 0,
				isEstimate: cached.softwareIsEstimate,
			},
			grants,
			ai,
			earnings,
			referrals: REFERRALS_KEY_METRIC_POTENTIAL,
			error: null,
			seeded: true,
		};
	};

	const [state, setState] = useState(() => {
		const seeded = seedFromCompany(company);
		return (
			seeded || {
				loading: true,
				financing: 0,
				software: { pending: true, amount: 0, isEstimate: false },
				grants: null,
				ai: 0,
				earnings: null,
				referrals: REFERRALS_KEY_METRIC_POTENTIAL,
				error: null,
			}
		);
	});
	const recomputedFor = useRef(null);
	const lastPagePatchKey = useRef('');
	const getTokenRef = useRef(getToken);
	getTokenRef.current = getToken;

	// Re-seed instantly when AppContext company arrives with cache fields
	useEffect(() => {
		const seeded = seedFromCompany(company);
		if (!seeded) return;
		setState((prev) => ({
			...prev,
			loading: false,
			financing: seeded.financing ?? prev.financing,
			software:
				seeded.software.amount > 0 || !seeded.software.pending
					? seeded.software
					: prev.software,
			grants: seeded.grants != null ? seeded.grants : prev.grants,
			ai: seeded.ai ?? prev.ai,
			earnings: seeded.earnings != null ? seeded.earnings : prev.earnings,
		}));
	}, [
		company?.id,
		company?.fields?.KeyMetricGrants,
		company?.fields?.KeyMetricEarnings,
		company?.fields?.KeyMetricSoftware,
		company?.fields?.KeyMetricFinancing,
		company?.fields?.KeyMetricAI,
		company?.fields?.KeyMetricSoftwareIsEstimate,
	]);

	useEffect(() => {
		if (!enabled || !website) {
			setState((s) => ({ ...s, loading: false }));
			return;
		}

		let cancelled = false;
		lastPagePatchKey.current = '';
		// Keep seeded values visible; only mark loading if we have no useful seed
		setState((s) => ({
			...s,
			loading: s.grants == null && !s.financing && !s.ai && s.earnings == null,
			error: null,
		}));

		(async () => {
			try {
				const tokenFn = getTokenRef.current;
				const data = await fetchKeyMetrics({
					website,
					recompute: false,
					getToken: tokenFn,
				});
				if (cancelled) return;

				const earnings =
					data.earnings ??
					sumEarningsTotal({
						financing: data.financing ?? 0,
						software: data.software ?? 0,
						grants: data.grants ?? 0,
						ai: data.ai ?? 0,
					});

				setState((prev) => ({
					loading: false,
					financing: data.financing ?? prev.financing ?? 0,
					software: {
						pending: data.software == null && prev.software?.pending,
						amount: data.software ?? prev.software?.amount ?? 0,
						isEstimate: parseSoftwareIsEstimateFlag(
							data.softwareIsEstimate ?? prev.software?.isEstimate
						),
					},
					grants: data.grants != null ? data.grants : prev.grants,
					ai: data.ai ?? prev.ai ?? 0,
					earnings: earnings ?? prev.earnings,
					referrals: data.referrals ?? prev.referrals ?? REFERRALS_KEY_METRIC_POTENTIAL,
					error: null,
				}));

				if (data.persistErrors?.length && typeof console !== 'undefined') {
					console.warn(
						'[key-metrics] Some KeyMetric* fields failed to save (check Airtable field names/types):',
						data.persistErrors
					);
				}

				// Backup: if GET could not persist an empty company cache, force a POST write
				if (data.persisted === false) {
					softPatchKeyMetrics({
						website,
						getToken: getTokenRef.current,
						patch: {
							financing: data.financing,
							ai: data.ai,
							software: data.software,
							softwareIsEstimate: data.softwareIsEstimate,
							...(data.grants != null ? { grants: data.grants } : {}),
							...(earnings != null ? { earnings } : {}),
						},
					});
				}

				const needsRecompute =
					data.grants == null || data.stale || data.source?.grants === 'missing';
				if (needsRecompute && recomputedFor.current !== website) {
					recomputedFor.current = website;
					fetchKeyMetrics({
						website,
						recompute: true,
						getToken: getTokenRef.current,
					})
						.then((fresh) => {
							if (cancelled) return;
							const freshEarnings =
								fresh.earnings ??
								sumEarningsTotal({
									financing: fresh.financing ?? 0,
									software: fresh.software ?? 0,
									grants: fresh.grants ?? 0,
									ai: fresh.ai ?? 0,
								});
							setState((prev) => ({
								...prev,
								financing: fresh.financing ?? prev.financing,
								software: {
									pending: false,
									amount: fresh.software ?? prev.software.amount,
									isEstimate: parseSoftwareIsEstimateFlag(fresh.softwareIsEstimate),
								},
								grants: fresh.grants != null ? fresh.grants : prev.grants,
								ai: fresh.ai ?? prev.ai,
								earnings: freshEarnings,
								referrals: fresh.referrals ?? prev.referrals,
							}));
							if (fresh.persistErrors?.length && typeof console !== 'undefined') {
								console.warn(
									'[key-metrics] Recompute persist errors:',
									fresh.persistErrors
								);
							}
						})
						.catch(() => {
							/* keep fast-path values */
						});
				}
			} catch (err) {
				if (cancelled) return;
				setState((s) => ({
					...s,
					loading: false,
					error: err?.message || 'Failed to load key metrics',
				}));
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [website, enabled]);

	// When visiting a metric page, persist live totals that differ from company cache
	useEffect(() => {
		if (!enabled || !website || !activeMetricId || !live) return;

		const patch = buildActivePageMetricsPatch(activeMetricId, {
			live,
			cached: state,
		});
		if (!patch) return;

		const patchKey = `${website}:${activeMetricId}:${JSON.stringify(patch)}`;
		if (lastPagePatchKey.current === patchKey) return;
		lastPagePatchKey.current = patchKey;

		softPatchKeyMetrics({
			website,
			getToken: getTokenRef.current,
			patch,
		});

		// Optimistic local update so nav stays in sync without waiting on Airtable
		setState((prev) => {
			const next = { ...prev };
			if (patch.financing != null) next.financing = patch.financing;
			if (patch.ai != null) next.ai = patch.ai;
			if (patch.grants != null) next.grants = patch.grants;
			if (patch.earnings != null) next.earnings = patch.earnings;
			if (patch.software != null) {
				next.software = {
					pending: false,
					amount: patch.software,
					isEstimate: Boolean(patch.softwareIsEstimate),
				};
			}
			return next;
		});
	}, [
		enabled,
		website,
		activeMetricId,
		live?.financing,
		live?.ai,
		live?.grants,
		live?.earnings,
		live?.software?.amount,
		live?.software?.pending,
		live?.software?.isEstimate,
		state.financing,
		state.ai,
		state.grants,
		state.earnings,
		state.software?.amount,
	]);

	return state;
}

/** @deprecated kept for Summary imports that still call compute locally */
export function computeLocalGrantsAmount(companyFields, grants) {
	return computeGrantsAmount(companyFields, grants);
}
